import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DropDownList } from '@core/interfaces';
import { EventService, ListService, fromList } from '@core/services';

import { endpoints } from '@environment';

import {
  InventoryInquiryRequest,
  InventoryInquiryResponseBody,
  InventoryItemDetailRequest,
  InventoryItemDetailResponseBody,
  InventoryItemLotRequest,
  InventoryItemLotResponseBody,
} from '#types/inventory';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@inventory/constants';
import { InventoryActions, fromInventory } from '@inventory/state';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  inquiryGridPanelHeight$ = new BehaviorSubject<number>(0);
  itemDetailGridPanelHeight$ = new BehaviorSubject<number>(0);
  lotDetailGridPanelHeight$ = new BehaviorSubject<number>(0);

  inventoryClassList$ = this.store.select(fromList.selectInventoryClassList);
  inventoryStatusList$ = this.store.select(fromList.selectInventoryStatusList);
  productGroupList$ = this.store.select(fromList.selectProductGroupList);

  itemDetailData$ = this.store.select(fromInventory.selectItemDetailData);
  lotDetailData$ = this.store.select(fromInventory.selectLotDetailData);

  inventoryClassDropdownList$ = new BehaviorSubject<DropDownList>(null);
  inventoryStatusDropdownList$ = new BehaviorSubject<DropDownList>(null);
  productGroupDropdownList$ = new BehaviorSubject<DropDownList>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private listService: ListService,
    private store: Store,
    private translocoService: TranslocoService,
    private http: HttpClient,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });
  }

  requestLists() {
    this.store.dispatch(InventoryActions.RequestLists());
  }

  inventoryInquiry(payload: InventoryInquiryRequest) {
    return this.http.post(endpoints.inventory.inventoryInquiry, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_INVENTORY_INQUIRY),
      map((response: InventoryInquiryResponseBody) => response)
    );
  }

  inventoryItemDetail(payload: InventoryItemDetailRequest) {
    return this.http.post(endpoints.inventory.inventoryItemDetail, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_INVENTORY_ITEM_DETAIL),
      map((response: InventoryItemDetailResponseBody) => response)
    );
  }

  inventoryLotDetail(payload: InventoryItemLotRequest) {
    return this.http.post(endpoints.inventory.inventoryLotDetail, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_INVENTORY_LOT_DETAIL),
      map((response: InventoryItemLotResponseBody) => response)
    );
  }

  initializeDropdownLists() {
    this.inventoryClassList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.inventoryClassDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.inventoryStatusList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.inventoryStatusDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.productGroupList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.productGroupDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'descr',
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }
}
